import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { BrandLogo } from './brand-logo';

const ICONS = [
  'M3 11v2a1 1 0 001 1h2l5 4V6L6 10H4a1 1 0 00-1 1zM15 9a3 3 0 010 6M18 6a7 7 0 010 12',
  'M4 5h16v11H9l-5 4V5z',
  'M9 11a4 4 0 100-8 4 4 0 000 8zM2 21c1-4 4-6 7-6s6 2 7 6M17 11a3 3 0 100-6M22 21c-.5-3-2.5-5-5-5.5',
  'M4 6h16v14H4zM4 10h16M8 3v4M16 3v4',
  'M4 5a2 2 0 012-2h13v16H6a2 2 0 00-2 2V5zM4 19a2 2 0 012-2h13',
  'M12 3l2 6 6 2-6 2-2 6-2-6-6-2 6-2z',
  'M9 3h6v3H9zM7 4.5H5V21h14V4.5h-2M8 11h8M8 15h5',
  'M9 6h11M9 12h11M9 18h11M4 6l1 1 2-2M4 12l1 1 2-2M4 18l1 1 2-2',
  'M5 21V4M5 4h11l-2 4 2 4H5',
  'M2 9l10-5 10 5-10 5L2 9zM6 11v5c0 1.5 3 3 6 3s6-1.5 6-3v-5',
  'M3 17l6-6 4 4 8-8M15 7h6v6',
  'M4 20V4M4 20h16M8 16v-4M12 16V8M16 16v-6',
  'M12 3a9 9 0 100 18 9 9 0 000-18zM9.5 9.5a2.5 2.5 0 014.6 1.3c0 1.7-2.1 1.9-2.1 3.4M12 17.5v.01',
  'M12 3l2.7 5.6 6.1.9-4.4 4.3 1 6.1L12 17l-5.4 2.9 1-6.1L3.2 9.5l6.1-.9z',
];

type Props = {
  company: string;
  brand: string;
  iconUrl?: string | null;
  logoUrl?: string | null;
};

export const LaunchOverlay = memo(({ company, brand, iconUrl, logoUrl }: Props) => {
  const { t } = useTranslation();
  const initial = company.trim().charAt(0).toUpperCase() || 'A';

  return (
    <div className="FreeTrial__Launch" role="status" aria-label={t('authentication:trial_launch_title')}>
      <svg aria-hidden="true" width="100%" height="100%" className="FreeTrial__Launch__Dots">
        <defs>
          <pattern id="trial-launch-dots" width="22" height="22" patternUnits="userSpaceOnUse">
            <circle cx="2" cy="2" r="1.2" fill="#c7d2fe" fillOpacity=".14" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#trial-launch-dots)" />
      </svg>
      <div className="FreeTrial__Launch__Orbit" aria-hidden="true">
        {ICONS.map((path, index) => {
          const angle = Math.round((index * 360) / ICONS.length);
          const delay = `${(0.2 + (index % 5) * 0.28 + Math.floor(index / 5) * 0.1).toFixed(2)}s`;

          return (
            <div key={path} className="FreeTrial__Launch__Spoke" style={{ transform: `rotate(${angle}deg)` }}>
              <div className="FreeTrial__Launch__Converge" style={{ animationDelay: delay }}>
                <div
                  className="FreeTrial__Launch__Chip"
                  style={{ transform: `translate(-50%, -50%) rotate(${-angle}deg)` }}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#c7d2fe"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d={path} />
                  </svg>
                </div>
              </div>
            </div>
          );
        })}
        <span className="FreeTrial__Launch__Burst" />
        <svg className="FreeTrial__Launch__Ring" width="170" height="170" viewBox="0 0 170 170">
          <circle cx="85" cy="85" r="70" fill="none" stroke="rgba(199,210,254,.15)" strokeWidth="4" />
          <circle
            className="FreeTrial__Launch__RingDraw"
            cx="85"
            cy="85"
            r="70"
            fill="none"
            stroke="#2463eb"
            strokeWidth="4"
            strokeLinecap="round"
            strokeDasharray="440"
          />
        </svg>
        <div className="FreeTrial__Launch__Mark" style={{ '--trial-brand': brand } as React.CSSProperties}>
          {iconUrl || logoUrl ? (
            <BrandLogo
              className="FreeTrial__Launch__Logo"
              iconUrl={iconUrl}
              logoUrl={logoUrl}
              name={company}
            />
          ) : (
            <div className="FreeTrial__Launch__Initial" style={{ backgroundColor: brand }}>{initial}</div>
          )}
        </div>
      </div>
      <div className="FreeTrial__Launch__Copy">
        <div className="FreeTrial__Launch__Title">{t('authentication:trial_launch_title')}</div>
        <div className="FreeTrial__Launch__Sub">{t('authentication:trial_launch_subtitle', { name: company })}</div>
      </div>
    </div>
  );
});
